import React from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    ButtonGroup,
    Checkbox,
    Divider,
    Flex,
    Stack,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessage, FormattedMessage} from 'react-intl'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import ConfirmationModal from '../../../../components/confirmation-modal/index'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {buttonStyle} from './index.style'

export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item from your cart?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

export const SIGN_OUT_FROM_CHECKOUT_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Sign Out',
        id: 'confirmation_modal.checkout_sign_out.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage:
            'Are you sure you want to sign out? You will need to sign back in to proceed with your current order.',
        id: 'confirmation_modal.checkout_sign_out.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, sign out',
        id: 'confirmation_modal.checkout_sign_out.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, sign out',
        id: 'confirmation_modal.checkout_sign_out.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'cancel',
        id: 'confirmation_modal.checkout_sign_out.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'cancel',
        id: 'confirmation_modal.checkout_sign_out.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

/**
 * Renders secondary actions on a product-item card in the form of a button group.
 * Represents other actions you want the user to perform with the product-item
 * (eg.: Remove or Edit or Add to wishlist for cart items)
 */
const CartSecondaryButtonGroup = ({
    onAddToWishlistClick = noop,
    onEditClick = noop,
    onRemoveItemClick = noop,
    onIsAGiftChange = noop,
    isAGift = false,
    isEGiftCard
}) => {
    const variant = useItemVariant()
    const navigate = useNavigation()
    const {data: customer} = useCurrentCustomer()
    const modalProps = useDisclosure()

    const showRemoveItemConfirmation = () => {
        modalProps.onOpen()
    }

    const handleRemoveItem = async () => {
        onRemoveItemClick(variant)
    }

    const handleAddToWishlist = () => {
        if (customer.isRegistered) {
            onAddToWishlistClick(variant)
        } else {
            navigate('/account')
        }
    }

    return (
        <>
            <Stack
                direction={{base: 'column', lg: 'row'}}
                alignItems={{base: 'flex-start', lg: 'flex-end'}}
                justifyContent={{base: 'flex-start', lg: 'space-between'}}
                divider={<Divider display={{base: 'block', lg: 'none'}} />}
            >
                <ButtonGroup spacing={{base: '10px', lg: '15px'}}>
                    {!isEGiftCard && (
                        <>
                            <Button
                                variant="link"
                                sx={buttonStyle}
                                onClick={() => onEditClick(variant)}
                            >
                                <FormattedMessage
                                    defaultMessage="EDIT"
                                    id="cart_secondary_button_group.edit"
                                />
                            </Button>
                            <span>|</span>
                        </>
                    )}
                    <Button variant="link" sx={buttonStyle} onClick={showRemoveItemConfirmation}>
                        <FormattedMessage
                            defaultMessage="REMOVE"
                            id="cart_secondary_button_group.remove"
                        />
                    </Button>
                    {!isEGiftCard && (
                        <>
                            <span>|</span>
                            <Button
                                variant="link"
                                sx={buttonStyle}
                                onClick={handleAddToWishlist} // Use new handler
                            >
                                <FormattedMessage
                                    defaultMessage="ADD TO WISHLIST"
                                    id="cart_secondary_button_group.added_to_wishlist"
                                />
                            </Button>
                        </>
                    )}
                </ButtonGroup>
            </Stack>
            <ConfirmationModal
                {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={handleRemoveItem}
                {...modalProps}
            />
        </>
    )
}

CartSecondaryButtonGroup.propTypes = {
    onClick: PropTypes.func,
    onEditClick: PropTypes.func,
    onAddToWishlistClick: PropTypes.func,
    onRemoveItemClick: PropTypes.func,
    onIsAGiftChange: PropTypes.func,
    isAGift: PropTypes.bool,
    isEGiftCard: PropTypes.bool
}

export default CartSecondaryButtonGroup
